import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { UserIdService } from '../../../../../core/auth/user-auth/facade';
import { LoginLogoutActionTypes } from '../../../../../core/auth/user-auth/store/actions/login-logout.action';
import { concatLatestFrom, normalizeHttpError, withdrawOn } from '../../../../../core/util';
import { TinyCartConnector } from '../../connector/tiny-cart.connector';
import { CartActions, CartModuleLoadedActions } from '../actions';
import { TinyCartActions } from '../actions/tiny-cart.action';
import { StateWithCart } from '../cart-state';
import { CartSelectors } from '../selectors';

@Injectable()
export class TinyCartEffects {
  private contextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  loadTinyCarts$: Observable<TinyCartActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.TinyCartActionTypes.LoadTinyCarts),
      map((action: CartActions.LoadTinyCarts) => action.payload),
      switchMap((payload) =>
        this.connector.load(payload).pipe(
          map((data) => new CartActions.LoadTinyCartsSuccess(data)),
          catchError((error) => of(new CartActions.LoadTinyCartsFail(normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  loadTinyCart$: Observable<TinyCartActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.TinyCartActionTypes.LoadTinyCart),
      map((action: CartActions.LoadTinyCart) => action.payload),
      switchMap((payload) =>
        this.connector.loadSpecific(payload.userId, payload.cartId).pipe(
          map((data) => new CartActions.LoadTinyCartSuccess(data)),
          catchError((error) => of(new CartActions.LoadTinyCartFail(normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  loadTinyCartIfMissingWhenLoadingCart$: Observable<TinyCartActions> = createEffect(() =>
    this.actions$.pipe(
      ofType<CartActions.LoadCartSuccess>(CartActions.CartActionTypes.LoadCartSuccess),
      map((action) => action.payload),
      filter((payload) => payload.extraData?.active),
      concatLatestFrom((payload) => this.store.pipe(select(CartSelectors.getTinyCart, { cartType: payload.cartType }))),
      filter(([payload, tinyCart]) => !tinyCart || payload.cartId !== tinyCart.code),
      map(([payload]) => payload),
      map((payload) => new CartActions.LoadTinyCart({ cartId: payload.cartId, userId: payload.userId })),
      withdrawOn(this.contextChange$)
    )
  );

  loadTinyCartsAtLogin$: Observable<TinyCartActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginLogoutActionTypes.Login),
      switchMap(() =>
        this.userIdService.takeUserId(true).pipe(
          map((userId) => new CartActions.LoadTinyCarts(userId)),
          catchError(() => EMPTY)
        )
      )
    )
  );

  loadTinyCartsAtInitializationIfLoggedIn$: Observable<TinyCartActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartModuleLoadedActions.CART_MODULE_LOADED),
      switchMap(() =>
        this.userIdService.takeUserId(true).pipe(
          map((userId) => new CartActions.LoadTinyCarts(userId)),
          catchError(() => EMPTY)
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<StateWithCart>,
    private connector: TinyCartConnector,
    private userIdService: UserIdService
  ) {}
}
